import React, { useMemo } from 'react'
import FormInput from './form-input/FormInput'
import { FieldValues, Path, PathValue, RegisterOptions } from 'react-hook-form'
import {
  SSNPropTypes,
  VALUE_CONSTANTS,
  NAMESPACE,
  REGEX,
  FORMAT_PATTERN,
} from '../utils'

const SSNInput = <T extends FieldValues>({
  name,
  label,
  disabled = false,
  placeholder = '',
  defaultValue = '',
  register,
  errors,
  formatPattern = FORMAT_PATTERN.SSN,
  setValue,
  dataQaId,
  customStyles = {},
  maxLength,
  isHelperTextVisible = '',
  ...rest
}: SSNPropTypes<T>) => {
  const formatInput = (inputValue: string, pattern: string) => {
    const digits = inputValue.replace(/\D/g, '')
    let formattedValue = ''
    let patternIndex = VALUE_CONSTANTS.ZERO
    let digitIndex = VALUE_CONSTANTS.ZERO

    while (digitIndex < digits.length && patternIndex < pattern.length) {
      if (pattern[patternIndex] === 'X') {
        formattedValue += digits[digitIndex]
        digitIndex++
      } else {
        formattedValue += pattern[patternIndex]
      }
      patternIndex++
    }

    return formattedValue
  }

  const SSNPlaceholder = useMemo(() => {
    if (!maxLength || maxLength === 9) {
      return FORMAT_PATTERN.SSN
    } else {
      return 'X'.repeat(maxLength)
    }
  }, [maxLength])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    let formattedValue = inputValue.replace(/-/g, '')
    if (!maxLength || Number(maxLength) === 9) {
      formattedValue = formatInput(inputValue, formatPattern)
    } else {
      formattedValue = formattedValue.replace(/\D/g, '')
      formattedValue = formattedValue.slice(0, maxLength)
    }
    setValue?.(name, formattedValue as PathValue<T, Path<T>>)
  }

  return (
    <FormInput
      name={name}
      label={label}
      disabled={disabled}
      errors={errors}
      placeholder={placeholder || SSNPlaceholder}
      defaultValue={defaultValue}
      onChange={handleChange}
      register={register}
      customStyles={{ ...customStyles }}
      dataQaId={dataQaId ?? ''}
      {...rest}
      validation={{
        ...((!maxLength || maxLength === 9
          ? {
              pattern: {
                value: REGEX.SSN,
                message: NAMESPACE.INVALID_SSN_NUMBER,
              },
            }
          : {
              minLength: {
                value: maxLength,
                message: NAMESPACE.INVALID_SSN_NUMBER,
              },
              maxLength: {
                value: maxLength,
                message: NAMESPACE.INVALID_SSN_NUMBER,
              },
            }) as RegisterOptions<T, Path<T>>),
      }}
      isHelperTextVisible={isHelperTextVisible}
    />
  )
}

export default SSNInput
